import ky from "ky";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useState } from "react";
import { useForm } from "react-hook-form";

import { ProfileType, STRICT_US_STATES, logError, useTheme } from "@up/data";
import { LoadingModal } from "@up/ui";

import {
  Email,
  FirstName,
  LastName,
  ProgramSelection,
  State,
  TopicSelection,
  Type,
} from "../form-fields";
import GenericError from "../GenericError";
import { Legal } from "../Legal";
import { ViewSyllabi } from "../ViewSyllabi";

import type { UserType } from "../form-fields";
import type { GeoData } from "@up/data";

type SaveState = "idle" | "loading" | "error" | "success";
type FormBoolean = "true" | "false";

const BASE_URL = process.env.NEXT_PUBLIC_HALL_OF_RECORDS_URI as string;

const DEFAULT_VALUES: {
  formId: string;
  age?: string;
  crossPartnerMarketingOptIn?: FormBoolean;
  email: string;
  firstName: string;
  lastName: string;
  programs?: string[];
  state?: string;
  topics?: string[];
  type: UserType;
} = {
  formId: "",
  email: "",
  firstName: "",
  lastName: "",
  type: "",
};

export const Form = function ({
  close,
  modalId,
  onSuccess,
  topics,
  trigger = "unknown",
  geoData,
}: {
  close: () => void;
  modalId: string;
  onSuccess: () => void;
  topics?: string[];
  trigger?: string;
  geoData: GeoData;
}) {
  const theme = useTheme();
  const reducedMotion = useReducedMotion();
  const [saveStatus, setSaveStatus] = useState<SaveState>("idle");

  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    watch,
  } = useForm({
    defaultValues: DEFAULT_VALUES,
    shouldUnregister: true,
  });

  const formValues = watch();

  const onSubmit = handleSubmit(async (data: typeof DEFAULT_VALUES) => {
    setSaveStatus("loading");

    if (!data) {
      return;
    }

    const payload = {
      ...data,
      crossPartnerMarketingOptIn: data.crossPartnerMarketingOptIn === "true",
      geoData: geoData,
      location: JSON.parse(JSON.stringify(window.location)),
      formId: modalId,
      touppAccept: true,
    };

    try {
      await ky.post(BASE_URL + `/v2/c/lc/${theme.partnerID}`, {
        json: payload,
      });
      setSaveStatus("success");
      onSuccess();
    } catch (err) {
      logError({ action: "LEAD_CAPTURE_SUBMIT", error: err });
      setSaveStatus("error");
    }
  });

  const TYPE_SELECTED = formValues["type"] !== "";
  const IS_STUDENT = formValues["type"] === ProfileType.Student;
  const IS_GUARDIAN = formValues["type"] === ProfileType.Guardian;
  const IS_STRICT_US_STATE =
    geoData.isStrictUSState ||
    STRICT_US_STATES.includes(formValues.state ?? "");

  if (saveStatus === "success") {
    return <ViewSyllabi close={close} />;
  }

  return (
    <form
      onSubmit={onSubmit}
      className="flex flex-col gap-4"
      data-testid="lead-capture-form"
    >
      {saveStatus === "loading" ? <LoadingModal /> : null}

      <p className="mb-4">
        Share your information below to hear more about our program and receive
        access to course syllabi.
      </p>

      <Type
        control={control}
        formErrors={errors}
        formValues={formValues}
        options={[
          {
            value: "Student",
            display: "Student",
          },
          {
            value: "Counselor",
            display: "College Advisor",
          },
        ]}
        register={register}
      />

      <AnimatePresence>
        {TYPE_SELECTED ? (
          <motion.div
            className="flex flex-col gap-4"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{
              ease: [0.87, 0, 0.13, 1],
              duration: reducedMotion ? 0 : 0.75,
            }}
          >
            <hr />

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <FirstName
                formErrors={errors}
                formValues={formValues}
                register={register}
              />

              <LastName
                formErrors={errors}
                formValues={formValues}
                register={register}
              />
            </div>

            {IS_STUDENT ? (
              <div>
                <Email
                  formErrors={errors}
                  formValues={formValues}
                  register={register}
                />
              </div>
            ) : (
              <div>
                <Email
                  formErrors={errors}
                  formValues={formValues}
                  register={register}
                />
              </div>
            )}

            {geoData.isDomestic ? (
              <State
                control={control}
                formErrors={errors}
                formValues={formValues}
                register={register}
              />
            ) : null}

            <AnimatePresence initial={false}>
              {IS_STUDENT || IS_GUARDIAN ? (
                <>
                  <hr />
                  <motion.div
                    className="flex flex-col gap-4 mb-2"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{
                      ease: [0.87, 0, 0.13, 1],
                      duration: reducedMotion ? 0 : 0.75,
                    }}
                  >
                    {topics ? (
                      <TopicSelection
                        formErrors={errors}
                        formValues={formValues}
                        register={register}
                        topics={topics}
                      />
                    ) : (
                      <ProgramSelection
                        baseURL={BASE_URL}
                        formErrors={errors}
                        formValues={formValues}
                        partnerID={theme.partnerID}
                        register={register}
                      />
                    )}
                  </motion.div>
                </>
              ) : null}
            </AnimatePresence>

            <hr />

            <Legal
              age={"18"}
              isDomestic={geoData.isDomestic}
              isStrictUSState={IS_STRICT_US_STATE}
              isStudent={IS_STUDENT}
              register={register}
            />
          </motion.div>
        ) : null}
      </AnimatePresence>

      {saveStatus === "error" ? <GenericError /> : null}

      <button
        className={`${theme.buttonPrimaryClass} w-full ${
          saveStatus === "loading" ? "cursor-wait opacity-50" : ""
        }`}
        disabled={saveStatus === "loading"}
        type="submit"
      >
        Request Info
      </button>
    </form>
  );
};
